<script setup>
/**
 * Extrato do agente — uma linha por cliente (Portela, 17/10/2026).
 * Fase 2 do ESTUDO-relatorio-por-agente.md.
 * Fase 4b do ESTUDO-fila-e-tempo-de-atendimento.md (os quatro relógios).
 *
 * POR QUE ESTA TELA EXISTE
 * As colunas do relatório são médias, e média sem como conferir é fé. Quando o
 * "Tempo Médio de Resolução" de alguém dá 21 horas, não havia nenhuma tela que
 * mostrasse QUAIS conversas fizeram esse número — e a resposta quase sempre é
 * uma conversa que ficou aberta o fim de semana, não atendimento ruim.
 *
 * O QUE ELA MOSTRA, e que o resumo não mostra
 * - **Atendeu ou só fechou**: a linha em que ele resolveu sem falar aparece
 *   marcada, com nome do cliente e hora.
 * - **A conta aberta**: espera total = com o bot + na fila + com ele. É a
 *   divisão que separa "agente lerdo" de "cliente dormiu na fila".
 * - **Tempo de atendimento**, da primeira mensagem DELE até a resolução — e não
 *   a vida da conversa, que é o que a coluna nativa mede.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AgentLedgerAPI from 'dashboard/api/agentLedger';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  userId: { type: [Number, String], required: true },
  from: { type: [Number, String], default: null },
  to: { type: [Number, String], default: null },
});

const { t } = useI18n();

const linhas = ref([]);
const meta = ref({});
const pagina = ref(1);
const carregando = ref(false);
const erro = ref('');

const POR_PAGINA = 25;

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil((meta.value.total_count || 0) / POR_PAGINA))
);

const dataHora = epoch => {
  if (!epoch) return '--';
  return new Date(epoch * 1000).toLocaleString();
};

/**
 * Duração em horas/minutos. Devolve "--" quando não há valor — e aqui o nulo é
 * informação: "ele nunca falou nesta conversa", ou "esta conversa é mais antiga
 * que o registro de atribuição". Diferente de zero, que afirmaria algo.
 */
const duracao = segundos => {
  if (segundos === null || segundos === undefined) return '--';
  const s = Math.max(0, Number(segundos));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min`;
  return `${s}s`;
};

/**
 * "Com ele" mostra o número JUSTO — o que desconta turno fechado — e guarda o
 * cru no title. Mostrar o cru na coluna seria repetir o erro que esta tela
 * existe para corrigir: cobrar do agente a madrugada em que ele não estava.
 *
 * Sem jornada cadastrada não há o que descontar, então o cru é o que há.
 */
const comEle = linha => {
  const justo = linha.tempo_com_ele_de_pe;
  if (justo === null || justo === undefined) return duracao(linha.tempo_com_ele);
  return duracao(justo);
};

const comEleTitulo = linha => {
  const cru = linha.tempo_com_ele;
  const justo = linha.tempo_com_ele_de_pe;
  if (cru === null || cru === undefined) return '';
  if (justo === null || justo === undefined) return t('AGENT_LEDGER.COM_ELE_SEM_JORNADA');
  if (justo === cru) return t('AGENT_LEDGER.COM_ELE_IGUAL');
  return t('AGENT_LEDGER.COM_ELE_DESCONTADO', { cru: duracao(cru) });
};

const descontou = linha =>
  linha.tempo_com_ele_de_pe !== null &&
  linha.tempo_com_ele_de_pe !== undefined &&
  linha.tempo_com_ele !== null &&
  linha.tempo_com_ele_de_pe < linha.tempo_com_ele;

// Os cartões do topo: as médias do período, não da página. Nulo quando o
// período é grande demais para calcular (o builder avisa em medias_do_periodo).
const cartoes = computed(() => [
  {
    chave: 'total',
    rotulo: t('AGENT_LEDGER.CARD_CONVERSAS'),
    valor: (meta.value.total_count ?? 0).toLocaleString(),
    nota: t('AGENT_LEDGER.CARD_CONVERSAS_NOTA'),
  },
  {
    chave: 'atendidos',
    rotulo: t('AGENT_LEDGER.CARD_ATENDIDOS'),
    valor: (meta.value.attended_count ?? 0).toLocaleString(),
    nota: t('AGENT_LEDGER.CARD_ATENDIDOS_NOTA'),
  },
  {
    chave: 'fila',
    rotulo: t('AGENT_LEDGER.CARD_FILA'),
    valor: duracao(meta.value.avg_na_fila),
    nota: t('AGENT_LEDGER.CARD_FILA_NOTA'),
  },
  {
    chave: 'comele',
    rotulo: t('AGENT_LEDGER.CARD_COM_ELE'),
    valor: duracao(meta.value.avg_com_ele_de_pe ?? meta.value.avg_com_ele),
    nota: t('AGENT_LEDGER.CARD_COM_ELE_NOTA'),
  },
]);

const buscar = async () => {
  if (!props.userId) return;
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await AgentLedgerAPI.get({
      userId: props.userId,
      from: props.from,
      to: props.to,
      page: pagina.value,
      perPage: POR_PAGINA,
    });
    linhas.value = data.payload || [];
    meta.value = data.meta || {};
  } catch (e) {
    erro.value = t('AGENT_LEDGER.ERRO');
    linhas.value = [];
  } finally {
    carregando.value = false;
  }
};

// Trocar de período volta para a primeira página: continuar na página 7 de um
// recorte que agora tem 2 páginas mostraria vazio e pareceria "não atendeu".
watch(
  () => [props.userId, props.from, props.to],
  () => {
    pagina.value = 1;
    buscar();
  }
);
watch(pagina, buscar);
onMounted(buscar);
</script>

<template>
  <div class="mt-6">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('AGENT_LEDGER.TITULO') }}
      </h3>
      <span v-if="meta.total_count" class="text-sm text-n-slate-11">
        {{
          t('AGENT_LEDGER.RESUMO', {
            total: meta.total_count,
            atendidos: meta.attended_count,
            fechados: meta.closed_without_reply_count,
          })
        }}
      </span>
    </div>

    <div v-if="carregando" class="py-10">
      <Spinner class="mx-auto" />
    </div>

    <p v-else-if="erro" class="py-6 text-sm text-n-ruby-11">{{ erro }}</p>

    <p v-else-if="!linhas.length" class="py-6 text-sm text-n-slate-11">
      {{ t('AGENT_LEDGER.VAZIO') }}
    </p>

    <template v-else>
      <!-- As médias do período. Ficam acima da tabela porque a pergunta chega
           em forma de média ("por que 21 horas?") e as linhas são a resposta. -->
      <div class="grid grid-cols-2 gap-3 mb-4 md:grid-cols-4">
        <div
          v-for="cartao in cartoes"
          :key="cartao.chave"
          class="px-4 py-3 rounded-lg bg-n-solid-2 outline-1 outline outline-n-container"
        >
          <p class="mb-1 text-xs uppercase text-n-slate-11">{{ cartao.rotulo }}</p>
          <p class="text-lg font-medium text-n-slate-12">{{ cartao.valor }}</p>
          <p class="text-xs text-n-slate-10">{{ cartao.nota }}</p>
        </div>
      </div>

      <!-- Período grande demais para as médias: dizer isso é melhor que mostrar
           "--" sem explicação, que pareceria defeito. -->
      <p
        v-if="meta.medias_do_periodo === false"
        class="mb-3 text-xs text-n-slate-10"
      >
        {{ t('AGENT_LEDGER.PERIODO_GRANDE') }}
      </p>

      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11 border-b border-n-weak">
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.CLIENTE') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.PRIMEIRA_MSG_CLIENTE') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.PRIMEIRA_RESPOSTA') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.ESPERA_TOTAL') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.COM_O_BOT') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.NA_FILA') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.COM_ELE') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.MENSAGENS') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.RESOLVIDA_EM') }}</th>
              <th class="py-2 pr-4">{{ t('AGENT_LEDGER.TEMPO_ATENDIMENTO') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in linhas"
              :key="linha.conversation_id"
              class="border-b border-n-weak/50"
            >
              <td class="py-2 pr-4">
                <a
                  :href="`/app/accounts/${$route.params.accountId}/conversations/${linha.display_id}`"
                  class="text-n-slate-12 hover:underline"
                >
                  {{ linha.cliente || t('AGENT_LEDGER.SEM_NOME') }}
                </a>
                <!-- A marca do caso que motivou tudo isto: resolveu e nunca falou. -->
                <span
                  v-if="!linha.atendeu"
                  class="ml-2 px-1.5 py-0.5 rounded text-xs bg-n-amber-3 text-n-amber-11"
                >
                  {{ t('AGENT_LEDGER.SEM_FALAR') }}
                </span>
              </td>
              <td class="py-2 pr-4">{{ dataHora(linha.primeira_msg_cliente) }}</td>
              <td class="py-2 pr-4">{{ dataHora(linha.primeira_resposta_agente) }}</td>
              <td class="py-2 pr-4">{{ duracao(linha.espera_total) }}</td>
              <td class="py-2 pr-4 text-n-slate-11">
                {{ duracao(linha.tempo_com_o_bot) }}
              </td>
              <td class="py-2 pr-4">{{ duracao(linha.tempo_na_fila) }}</td>
              <td class="py-2 pr-4" :title="comEleTitulo(linha)">
                {{ comEle(linha) }}
                <!-- O ponto avisa que ali houve desconto de turno fechado; sem
                     ele, o número menor pareceria erro de conta. -->
                <span v-if="descontou(linha)" class="text-n-teal-11">·</span>
              </td>
              <td class="py-2 pr-4">{{ linha.mensagens_do_agente }}</td>
              <td class="py-2 pr-4">{{ dataHora(linha.resolvida_em) }}</td>
              <td class="py-2 pr-4">{{ duracao(linha.tempo_de_atendimento) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="mt-3 text-xs text-n-slate-10">
        {{ t('AGENT_LEDGER.RODAPE') }}
      </p>

      <div v-if="totalPaginas > 1" class="flex items-center gap-3 mt-3 text-sm">
        <button
          class="px-2 py-1 rounded border border-n-weak disabled:opacity-40"
          :disabled="pagina <= 1"
          @click="pagina -= 1"
        >
          ‹
        </button>
        <span class="text-n-slate-11">{{ pagina }} / {{ totalPaginas }}</span>
        <button
          class="px-2 py-1 rounded border border-n-weak disabled:opacity-40"
          :disabled="pagina >= totalPaginas"
          @click="pagina += 1"
        >
          ›
        </button>
      </div>
    </template>
  </div>
</template>
